import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { spaceSlug } from '@/lib/host'

/**
 * One route table for both surfaces.
 *
 * `meta.scope` decides which one a route belongs to, and the guard below keeps
 * them apart: a space route is only reachable on a space subdomain, an apex
 * route only on the apex. That is what lets a single deployment serve both
 * without the dashboard leaking onto the marketing host or vice versa.
 */
declare module 'vue-router' {
  interface RouteMeta {
    scope?: 'apex' | 'space' | 'any'
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'front',
    // Apex and space hosts share this path; FrontPage picks the right page for
    // the host, so `scope` has to admit both.
    component: () => import('@/pages/FrontPage.vue'),
    meta: { scope: 'any' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { scope: 'any' },
  },
  {
    // Where the OAuth callback sends an address that the server's allow list
    // did not admit. Reached only by that redirect, so it is a statement, not a
    // page anyone navigates to — hence no session work here at all.
    path: '/waitlist',
    name: 'waitlist',
    component: () => import('@/pages/WaitlistPage.vue'),
    meta: { scope: 'any' },
  },
  {
    path: '/create-space',
    name: 'create-space',
    component: () => import('@/pages/CreateSpacePage.vue'),
    meta: { scope: 'apex' },
  },
  // "Not a member of this space" is not a route: ConsoleLayout resolves it for
  // every space page at once, so a bad space URL cannot render a half-built
  // dashboard around an error.
  {
    path: '/members',
    name: 'members',
    component: () => import('@/pages/space/MembersPage.vue'),
    meta: { scope: 'space' },
  },
  {
    path: '/credits',
    name: 'credits',
    component: () => import('@/pages/space/CreditUsagePage.vue'),
    meta: { scope: 'space' },
  },
  {
    path: '/artifacts',
    name: 'artifacts',
    component: () => import('@/pages/space/ArtifactsPage.vue'),
    meta: { scope: 'space' },
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/pages/space/AnalyticsPage.vue'),
    meta: { scope: 'space' },
  },
  {
    path: '/billing',
    name: 'billing',
    component: () => import('@/pages/space/BillingPage.vue'),
    meta: { scope: 'space' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/pages/space/SettingsPage.vue'),
    meta: { scope: 'space' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { scope: 'any' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach((to) => {
  if (spaceSlug) {
    // On a space host the dashboard is the surface: `/create-space` and other
    // apex-only routes have no space to belong to, so they fall back to the
    // front page. `/` itself is shared — SpaceFrontPage renders the space's
    // front page for a visitor who is not a member, and hands off to the
    // console once a member's session resolves.
    if (to.meta.scope === 'apex') {
      return { path: '/', replace: true }
    }
    return true
  }

  // On the apex, a space route has no space to belong to.
  if (to.meta.scope === 'space') {
    return { path: '/', replace: true }
  }
  return true
})
